import { apiClient, getApiBaseUrl } from '../core/api/apiClient';

export interface SessionSummary {
  session_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface StoredMessage {
  role: 'human' | 'assistant';
  content: string;
  tool_used?: string | null;
  timestamp: string;
}

export interface SessionDetail extends SessionSummary {
  messages: StoredMessage[];
}

export interface StreamCallbacks {
  onSession?: (sessionId: string) => void;
  onToken?: (delta: string) => void;
  onToolCall?: (tool: string) => void;
  onToolDone?: (tool: string) => void;
  onDone?: () => void;
  onError?: (msg: string) => void;
}

export const chatService = {
  createSession: async (): Promise<SessionSummary> => {
    return apiClient<SessionSummary>('/chat/sessions', { method: 'POST' });
  },

  listSessions: async (): Promise<SessionSummary[]> => {
    return apiClient<SessionSummary[]>('/chat/sessions');
  },

  loadSession: async (sessionId: string): Promise<SessionDetail> => {
    return apiClient<SessionDetail>(`/chat/sessions/${sessionId}`);
  },

  deleteSession: async (sessionId: string): Promise<{ deleted: boolean }> => {
    return apiClient<{ deleted: boolean }>(`/chat/sessions/${sessionId}`, { method: 'DELETE' });
  },

  streamMessage: (
    sessionId: string,
    message: string,
    llmProvider: 'nvidia' | 'claude' | 'llama' | 'deepseek',
    callbacks: StreamCallbacks
  ): AbortController => {
    const controller = new AbortController();
    
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token === 'authenticated' ? 'authenticated' : token}`;
        }
        const response = await fetch(`${getApiBaseUrl()}/chat/stream`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            session_id: sessionId || null,
            message: message,
            llm_provider: llmProvider
          }),
          signal: controller.signal
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Failed to stream: ${response.status} ${text}`);
        }
        if (!response.body) throw new Error("No response body");

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          
          buffer = parts.pop() || "";

          for (const part of parts) {
            if (!part.startsWith("data: ")) continue;
            
            const jsonStr = part.replace(/^data:\s*/, "");
            if (!jsonStr) continue;

            try {
              const data = JSON.parse(jsonStr);
              
              if (data.type === "session") {
                callbacks.onSession?.(data.session_id);
              } else if (data.type === "token") {
                callbacks.onToken?.(data.delta || "");
              } else if (data.type === "tool_call") {
                callbacks.onToolCall?.(data.tool);
              } else if (data.type === "tool_result") {
                callbacks.onToolDone?.(data.tool);
              } else if (data.type === "error") {
                callbacks.onError?.(data.message);
              } else if (data.type === "done") {
                // Done event explicitly sent by backend
              }
            } catch (e) {
              console.warn("Could not parse SSE JSON", jsonStr, e);
            }
          }
        }
        
        if (!controller.signal.aborted) {
          callbacks.onDone?.();
        }

      } catch (err: any) {
        if (!controller.signal.aborted) {
          console.error("Stream error:", err);
          callbacks.onError?.(err.message || "Failed to connect to chat server");
        }
      }
    })();

    return controller;
  },
};
